import { useEffect, useRef, useState } from 'react'
import { Modal, Properties, PropertyChips, PropertyDoors, PropertyFaces, formatPropertyDate, Editor, useDefineSheet, errText, Avatar, Button, EndRail, FocusGlyph, LucideGlyph, SelectionDefine, PropertiesIcon, CopyIcon, LinkIcon, useShownProperties } from '@ofself/shell'
import { getEditor, setTitleText } from '@ofself/shell/editor'
import '@ofself/shell/editor.css'
import { PROPERTIES, dateLabel, stampLabel, inheritedOf } from './model.js'
import Links from './Links.jsx'
import { WORD_PROPERTY_TYPES, wordPropertyValue } from './PropertyKinds.jsx'
import { api } from './api.js'
import ShareSheet from './ShareSheet.jsx'
import ExportSheet from './ExportSheet.jsx'
import TermTip from './TermTip.jsx'
import { qh } from './qh.js'

/* a menu row's label with a hotkey printed at its far edge — the kit's Menu
   has no hotkey column of its own, so the label carries it */

const ICONS = { created: '◷', edited: '✎', usedIn: '↻', usage: '∑', tags: '#', aliases: '≡', synonyms: '≈', antonyms: '≠', commitment: '✦', stance: '⊘', rather: '→', replacement: '⇄' }
/* what each property row says under Quick Help */
const HELP = {
  created: 'When this word was first written down.',
  edited: 'When its definition last changed.',
  usedIn: 'Where this word is leaned on: other words in the dictionary that cite it, and the notes in other apps where it was met. Each one is a door.',
  usage: 'How often this word is used across your apps: each time it — or one of its aliases — is written in an Ink note (counted the way Ink marks it — the whole word, any case, never in code or a note’s title), and in how many notes; then the same for its synonyms, and how many of your words cite it. Counted from your notes as they stand, at most a minute old. A note Ink keeps encrypted cannot be counted.',
  tags: 'Free labels for grouping words. Type one and press Return; × drops it. The list’s filter can narrow by tag.',
  aliases: 'Other forms of this same word — a plural, a tense, a spelling, an abbreviation. Each is marked and offered as you write, exactly like the word itself, and found by Find; none is a word of its own. Type one and press Return; × drops it.',
  synonyms: 'Words that mean the same — each a word of its own, linked both ways: adding one here lists this word on its page too, and taking it off takes it off both. Pick a word you have, or type a new one and it is made (in the dictionary you are in, or another you pick — a Hindi word for an English one). A new synonym reads this word’s definition, marked “via”, until you give it its own. Not chained: a synonym of a synonym is not one.',
  antonyms: 'Words that mean the opposite — linked both ways, like synonyms. A new antonym starts empty; open it to say what it means.',
  stance: 'Held or avoided. A held word is one you mean and lean on; an anti-word is one you refuse — kept in an anti-dictionary so you can say why, and so every editor that reads your dictionary catches you using it. Press to switch.',
  rather: 'For an anti-word, the words you would rather use — type one and press Return; the catch card offers each as a one-press swap. For a held word, the anti-words it stands in for.',
  replacement: 'For an anti-word, the one word from your dictionary it is translated into wherever text is written for you: as you dictate in Whisper, in a persona’s answer, in material read in. Optional: without it the anti-word is only caught. It must be a word you hold. A quotation is never translated.',
  commitment: 'Who has committed to this definition as it stands — their faces and their count. A commitment stamps the words at this revision; a later edit voids the stamps, so drift from what was agreed is visible.',
}

/* WHERE A COPY CAME FROM, in words: the ledger's `copied` act names the
   dictionary it left — the hand's own, a group's, or a friend's */
const cameFrom = v => !v || !v.from ? 'elsewhere'
  : v.from.kind === 'group' ? `the group ${v.from.name || ''}`.trim()
  : v.from.kind === 'friend' ? `${v.from.name || 'a friend'}'s dictionary`
  : 'their own dictionary'

/* THE VERSIONS, BEHIND THE ECLIPSE. They used to stand at the foot of every
   page, which put a word's whole past between the reader and the next word.
   The eclipse is where a record's grave and historical verbs belong.
     Each row is a SITTING (server rule, 2026-09-19), so two of them can fall
   in one afternoon: the row is dated to the second, by when its words were
   last written — an older row only knows when they were replaced. */
/* THE MODAL STAYS MOUNTED (bug 2026-09-19: "after I click out of share with
   the version history, the app freezes"). It used to return null the moment
   `open` fell — the Radix dialog underneath was torn out mid-dismiss and its
   body pointer lock was never released, so nothing behind it took a click
   again. Told `open` instead, it closes properly and lets go. */
function History({ open, onClose, word }) {
  const rows = [...(word.versions || [])].reverse()
  return <Modal open={open} onClose={onClose} label={`Version history of ${word.name}`} className="dt-history">
    <h2 className="dt-history__head" {...qh('Version history', 'Every wording this definition has had, newest first, and every commitment made or voided along the way.')}>Version history <em>{word.name}</em></h2>
    <ol className="dt-history__list" {...qh('The versions', 'The words as they stand now, the words as they were committed, and each earlier wording with its date. Lines marked with a stamp record who committed to which revision.')}>
      <li className="dt-history__row is-now">
        <span className="dt-history__at">now{(word.editedAt || word.updatedAt) ? ` · ${stampLabel(word.editedAt || word.updatedAt)}` : ''}</span>
        <span className="dt-history__body">{word.text || '—'}</span>
      </li>
      {/* the drift row: what stood when a member committed — shown only when
          the words have MOVED since. Judged on the plain reading against the
          plain reading (owner report 2026-09-19, "two committed messages"):
          committedAs is text, and against the HTML body it never matched. */}
      {word.committedAs && word.committedAs !== word.text && <li className="dt-history__row is-committed">
        <span className="dt-history__at">committed{word.committedAt ? ` · ${dateLabel(word.committedAt)}` : ''}</span>
        <span className="dt-history__body">{word.committedAs}</span>
      </li>}
      {rows.map((v, i) => v.kind
        ? <li key={i} className={`dt-history__row is-act is-${v.kind}`}>
            <span className="dt-history__at">{stampLabel(v.at)}</span>
            <span className="dt-history__body">
              {v.kind === 'stamp' ? `${(v.users || []).join(', ')} committed to rev ${v.rev}`
                : v.kind === 'withdraw' ? `${(v.users || []).join(', ')} uncommitted from rev ${v.rev}`
                : v.kind === 'copied' ? `${(v.users || []).join(', ')} brought this word here from ${cameFrom(v)}`
                : `an edit voided the commitments of ${(v.users || []).join(', ')} (rev ${v.rev})`}
            </span>
          </li>
        : <li key={i} className="dt-history__row">
            <span className="dt-history__at">{stampLabel(v.since || v.at)}</span>
            <span className="dt-history__body">{v.text || v.body}</span>
          </li>)}
    </ol>
    {!rows.length && !word.committedAs && <p className="dt-history__none">Nothing has replaced these words yet.</p>}
  </Modal>
}

/* the usage counts, one read for every word, held a minute (the server holds it as long) */
let usageHeld = null
const readUsage = () => {
  if (usageHeld && Date.now() - usageHeld.at < 60e3) return usageHeld.p
  const p = api.usage().catch(e => ({ ink: null, error: e.message }))
  usageHeld = { at: Date.now(), p }
  return p
}

export default function WordSheet({ word, draft, terms = [], avoidedTerms = [], words = [], prefs = {}, onPrefs, onChange, onCommit, onDelete, onDuplicate, onOpen, onOpenSlug, onMinted, onNotice, me, onStamp, onUnstamp, onKeep, linkOf, groups = [], activeGroup = null, onCopy, focus = false, onFocusChange, onLink, onUnlink, dictionaries = [], lens = '' }) {
  const [name, setName] = useState(word.name)
  const [body, setBody] = useState(word.body)
  /* A FRIEND'S WORD (2026-09-19): the page says whose it is, and does only
     what their grant allows — an edit verb opens the editor, the chips and
     the stamp; without it the word is read, exactly as they wrote it. */
  const shared = !!word.owner
  const readOnly = shared && word.access !== 'edit'
  const ownerName = shared ? (word.owner.display_name || word.owner.username || 'a friend') : ''
  // a group's word, in the group's dictionary: a copy of it can be yours
  const inGroup = !!activeGroup
  const [shareOpen, setShareOpen] = useState(false)
  const [exportOpen, setExportOpen] = useState(false)
  const [histOpen, setHistOpen] = useState(false)
  /* THE PROPERTIES STAND UNDER THE NAME (owner call 2026-10-12, the kit's
     <NoteHead>): the strip is on or off by the ⋯ menu's Properties row, open
     or collapsed by its chevron, both kept in this browser. */
  const [propsShown, showProps] = useShownProperties()
  const [copied, setCopied] = useState(false)
  const [text, setText] = useState(word.text || '')
  /* the dotted rule's tooltip: which term the pointer is on, and where it sits */
  const [tip, setTip] = useState(null)
  /* THE CARD WAITS FOR THE HAND (owner call 2026-09-19: "I should be able to
     travel to the tooltip to copy text"). Leaving the word no longer drops
     the card at once: it goes after a beat, and the beat is cancelled the
     moment the pointer lands on the card — so the gap between word and card
     can be crossed, and the card stays as long as the pointer is on it. */
  const tipTimer = useRef(null)
  const holdTip = () => { if (tipTimer.current) { clearTimeout(tipTimer.current); tipTimer.current = null } }
  const releaseTip = () => { holdTip(); tipTimer.current = setTimeout(() => setTip(null), 260) }

  /* ── HIGHLIGHT A PASSAGE, PRESS Aa (owner call 2026-09-19: "just like in ink
     I want to be able to highlight on a word to define it") ─────────────────
     The sheet is the kit's — the same component ink mounts, uprooted the same
     day — so all that lives here is the bubble it rides in and the two halves
     only this app knows: how to ASK (the dictionary's own Lexicographer route)
     and how to KEEP (a word, through the same door a hand always mints one).

     Ink's bubble is a formatting bar that happens to carry the Aa cell; the
     dictionary has no bar to hang it on, so this is the cell and nothing else
     — wearing the kit's own bubble dress so the two read as one gesture. */
  /* THE THREAD THE ANSWER CAME IN. The server asks the Lexicographer in one
     standing conversation per person per space and says which; every word
     this sheet lands cites it, so the identity wall's Personas pane can draw
     the words offered against the thread that offered them. */
  const thread = useRef(null)
  const cite = (anchor) => ({ conversationId: thread.current, quote: (anchor?.quote || '').slice(0, 300) })
  // the card's other forms: its aliases (the kit's card and the Lexicographer both say so)
  const formsOf = card => (card?.aliases || []).map(a => String(a || '').trim()).filter(Boolean)
  /* does a card name THIS word? — by the page's name or one of its aliases */
  const writesHere = (card) => {
    const term = String(card?.term || '').trim().toLowerCase()
    if (!term || !String(card?.definition || '').trim()) return false
    const names = [name || word.name, ...(word.aliases || [])].map(x => String(x || '').trim().toLowerCase())
    return names.includes(term)
  }
  /* write the card into this page: the definition as the body when the page
     is blank, after what is already written when it is not; its other forms
     onto the word's aliases */
  const writeHere = (card) => {
    const e = getEditor()
    const def = String(card.definition || '').trim()
    const html = `<p>${def.replace(/[&<>]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch]))}</p>`
    if (e && !e.isDestroyed) {
      // a blank body is one empty line: the card takes its place (the name, the doc's own attribute, stays)
      const end = e.state.doc.content.size
      e.chain().focus().insertContentAt(String(text || '').trim() ? end : { from: 0, to: end }, html).run()
    } else {
      setBody(b => (String(text || '').trim() ? `${b || ''}${html}` : html)); setText(t => (t ? `${t}\n${def}` : def))
      onChange?.({ body: String(text || '').trim() ? `${body || ''}${html}` : html, text: text ? `${text}\n${def}` : def })
    }
    const extra = formsOf(card).filter(a => a.toLowerCase() !== String(name || word.name).toLowerCase() && !(word.aliases || []).some(x => x.toLowerCase() === a.toLowerCase()))
    if (extra.length) onChange?.({ aliases: [...(word.aliases || []), ...extra] })
    onNotice?.('written into this word')
  }
  const define = useDefineSheet({
    ask: async (anchor) => {
      const r = await fetch('/api/define/extract', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(anchor || {}),
      })
      const data = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(errText(data, `the Lexicographer answered ${r.status}`))
      if (data.conversationId) thread.current = data.conversationId
      return data
    },
    keep: async (card, anchor) => {
      // THE PAGE'S OWN WORD IS WRITTEN HERE, NOT MINTED AGAIN (owner report
      // 2026-09-19: "creating a new entry with the title, highlighting it,
      // clicking define" answered "could not define: that word is already in
      // the dictionary" — the card's term was this page's name, and Keep
      // tried to make a second word by it). A term that names THIS word, by
      // its name or an alias, lands in this page's definition — as the first
      // paragraph when the page is blank, appended when it is not — and the
      // card's aliases join the word's. The editor's own change pipeline
      // saves it, the same as typing would.
      if (writesHere(card)) { writeHere(card); return true }
      // A WORD KEPT BY HAND IS COMMITTED AT BIRTH (the hand-is-ratified law):
      // the press IS the ratification, so nothing lands in the proposed queue.
      const body = String(card.definition || '').trim()
      const r = await api.create({
        name: card.term.trim(), body, text: body, status: 'committed',
        aliases: formsOf(card), origin_of: 'persona', origin: cite(anchor),
      }).catch(e => { throw new Error(e.message) })
      onMinted?.(r && r.word)
      return true
    },
    onError: (m) => onNotice?.(m),
    /* CLICKING OUT OFFERS, IT DOES NOT DISCARD (owner call 2026-09-19): every
       card left written in the sheet lands as a PROPOSED word — dim in the
       list, under the filter's Offered, committed or deleted later — exactly
       the standing Absorb gives what it finds. One pipeline for both doors.
       A name already in the dictionary is skipped (the server says 409). */
    onDismiss: async (cards, anchor) => {
      let made = 0
      for (const c of cards) {
        // this page's own term, left written: it is this page's definition
        if (writesHere(c)) { writeHere(c); continue }
        const body = String(c.definition || '').trim()
        try {
          await api.create({ name: c.term.trim(), body: `<p>${body.replace(/[&<>]/g, ch => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[ch]))}</p>`, text: body, status: 'proposed', aliases: formsOf(c), origin_of: 'persona', origin: cite(anchor) })
          made++
        } catch (e) { if (e.status !== 409) onNotice?.(`could not offer ${c.term}: ${e.message}`) }
      }
      if (made) { onMinted?.(null); onNotice?.(`${made} word${made === 1 ? '' : 's'} offered — under the filter's Offered, dim in the list, until you commit or delete ${made === 1 ? 'it' : 'them'}`) }
    },
  })

  // THE EDITOR IS FED LOCAL STATE ONLY. The page is keyed by the word's page
  // identity (stable across a local→real id swap), so this state lives as
  // long as the page does and no copy from the wire ever reaches the editor.
  // Every change goes out at once through onChange; the store coalesces the
  // wire and keeps what the hand typed over anything that comes back.
  const last = useRef({ name: word.name, body: word.body })
  // ONE EXCEPTION: a rename made elsewhere (the sidebar's Rename, 2026-09-19)
  // reaches the page — the store's name moved while the hand was not in the
  // name's field, so the field follows it. A name the hand typed here is never
  // overwritten: it equals last.current.name by the time the store echoes it.
  // THE ECHO IS TRIMMED, THE HAND IS NOT (audit 2026-10-02: "quiet budget"
  // typed at a human pace landed as "quietbudget"). The server trims a name, so
  // the echo of "quiet " came back "quiet" between the space and the "b" —
  // read as a rename from elsewhere, it rewrote the name and ate the space.
  // A name that differs from the hand's only by its outer spaces is the hand's.
  const same = (a, b) => String(a || '').trim() === String(b || '').trim()
  useEffect(() => {
    if (same(word.name, last.current.name) || same(word.name, name)) return
    last.current.name = word.name; setName(word.name)
    // the kit editor re-seats its document only on a docId change (Editor.jsx),
    // so the name is written into the head's field, the body and its history untouched
    setTitleText(word.name)
  }, [word.name]) // eslint-disable-line react-hooks/exhaustive-deps

  // the drift is judged on the PLAIN reading: a paragraph split is not a change of meaning
  /* COMMITMENT (owner call 2026-09-19): the stamps on the node as it stands —
     the stampers' faces, their count, and your own act. No roster: who COULD
     stamp is nobody's business here, only who has. */
  const live = (word.commitments || []).filter(c => c.live)
  /* THE STANCE (owner call 2026-09-30): an anti-word's page says so above
     the word, asks why instead of what, and names what you would say instead.
     A held word shows the anti-words that name it as their rather. */
  const avoids = word.stance === 'avoided'
  const fold = x => String(x || '').trim().toLowerCase()
  const heldNamed = t => words.find(w => !w.owner && w.stance !== 'avoided' && (fold(w.name) === fold(t) || (w.aliases || []).some(a => fold(a) === fold(t))))
  const setRather = list => onChange?.({ rather: list.map(t => { const w = heldNamed(t); return { term: w ? w.name : t, id: w && !String(w.id).startsWith('local_') ? w.id : null } }) })
  const insteadOf = avoids ? [] : words.filter(w => !w.owner && w.stance === 'avoided' && (w.rather || []).some(r => (r.id && r.id === word.id) || fold(r.term) === fold(word.name)))
  const setStance = next => onChange?.({ stance: next })
  const mine = me && live.find(c => c.user_id === me.user_id)
  /* A SYNONYM WITH NO WORDS OF ITS OWN reads its source's (owner call
     2026-10-04), said as such — and the moment a word is typed here it is this
     page's own, and the borrowed one steps away. One hop; never an antonym's. */
  const inherited = inheritedOf({ ...word, text }, words)
  // the uses, read when the plate stands
  const [usage, setUsage] = useState(null)
  useEffect(() => {
    if (!propsShown || draft || shared) return undefined
    let live = true
    readUsage().then(u => { if (live) setUsage(u) })
    return () => { live = false }
  }, [propsShown, draft, shared])

  /* THE ECLIPSE IS INK'S (owner call 2026-09-19: "the same eclipse menu for
     many of its menu items"): the page's layout pref first, then the editor's
     two undoing verbs with their hotkeys printed beside them (the teaching
     surface for whoever does not have ⌘Z in the hand yet), then everything
     that acts on the word, the grave verb last. Share folded in here as a
     row, as ink folded its seat — this menu is the one door. Ink's rows with
     no counterpart here (comments, move to, favorites) are not pretended. */
  const ed = () => { const e = getEditor(); return e && !e.isDestroyed ? e : null }
  // the definition's counts, read from the page's own text (it re-renders on
  // every edit, so the rail's readings and its history seats stay current)
  const plain = (ed()?.getText() ?? text ?? '').trim()
  const counts = { words: plain ? plain.split(/\s+/).length : 0, chars: plain.length }
  /* (Full width RETIRED 2026-09-19 — the page reads on the home's own
     measure now, so there is no narrower column for it to lift.) */
  /* words alone, no glyphs (owner call 2026-09-19: "remove all the icons") */
  // (Personas is the left rail's Agent now; Undo and Redo are this page's
  // rail seats — one door per verb, owner call 2026-09-23)
  const more = [
    { icon: <LucideGlyph name="share" />, label: 'Share…', onSelect: () => setShareOpen(true) },
    { icon: <LucideGlyph name="history" />, label: 'Version history', onSelect: () => setHistOpen(true) },
    // a toggle: the plate stands until this row puts it away, and says so
    { icon: <PropertiesIcon size={16} />, label: 'Properties', checked: !!propsShown, onSelect: () => showProps(!propsShown) },
    // THE STANCE, one press either way — only on your own word
    ...(shared ? [] : [avoids
      ? { label: 'Make it a word', onSelect: () => setStance('held') }
      : { label: 'Make it an anti-word', onSelect: () => setStance('avoided') }]),
    // a friend's word is not duplicated beside theirs — it is copied into yours
    ...(shared ? [] : [{ icon: <LucideGlyph name="duplicate" />, label: 'Duplicate', onSelect: () => onDuplicate?.() }]),
    ...(shared || inGroup ? [{ icon: <CopyIcon size={16} />, label: 'Copy into my dictionary', onSelect: onKeep }] : []),
    { icon: <LucideGlyph name="export" />, label: 'Export…', onSelect: () => setExportOpen(true) },
    { icon: <LinkIcon size={16} />, label: copied ? 'Link copied' : 'Copy link', onSelect: () => {
      // the address as the app reads it — under the owner's name, whoever they are
      navigator.clipboard?.writeText(linkOf ? linkOf(word) : `${location.origin}/word/${encodeURIComponent(word.slug)}`)
        .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1600) }).catch(() => {})
    } },
    // a friend's word is never deleted from theirs; a group's word may be, by a member
    ...(shared ? [] : [{ separator: true }, { icon: <LucideGlyph name="trash" />, label: 'Delete', danger: true, onSelect: onDelete }]),
  ]

  /* THE LEDGER, UNDER THE NAME (owner call 2026-10-12): every one of the rows
     is still here and still always here; an absent value is an em dash,
     never a missing row. */
  const properties = !draft && (
    <Properties readOnly={readOnly} mint={!readOnly}
      types={WORD_PROPERTY_TYPES} renderValue={wordPropertyValue({ words, onOpen })}
      properties={word.properties || []} onPropertiesChange={properties => onChange?.({ properties })}
      order={word.property_order || []} onOrderChange={property_order => onChange?.({ property_order })}
      {...qh('Word properties', 'The ledger behind the page: when it was made and last edited, where it is used, its tags, aliases, synonyms and antonyms, and who has committed to it. Every row is always here — a dash means empty.')}
      fields={PROPERTIES.map(p => {
        const f = { key: p.id, label: p.label, glyph: ICONS[p.id], help: HELP[p.id], keep: true }
        if (p.id === 'created') {
          // a copy says so at a glance: who brought it here, and from where
          const came = (word.versions || []).find(v => v.kind === 'copied')
          return { ...f, children: word.createdAt ? <>{formatPropertyDate(word.createdAt)}
            {came && <span className="dt-gloss">brought here by {(came.users || [])[0] || 'someone'} from {cameFrom(came)}</span>}</> : null }
        }
        if (p.id === 'edited') return { ...f, children: formatPropertyDate(word.updatedAt) }
        if (p.id === 'usedIn') {
          const items = [
            ...(word.usedIn || []).map(u => ({ key: u.id, label: u.name, onOpen: () => onOpen(u.id),
              ...qh(u.name, 'A word in this dictionary whose definition cites this one. Click to open it.') })),
            /* met elsewhere in the ecosystem — the anchors other apps wrote (Ink's Aa) */
            ...(word.usages || []).map((u, i) => ({ key: `u${i}`, label: `${u.app}${u.source_title ? ` · ${u.source_title}` : ''}`, href: u.href || undefined,
              title: [u.quote_prefix, u.quote, u.quote_suffix].filter(Boolean).join(''),
              ...qh(`Met in ${u.app}`, u.href ? 'This word was used in another app’s note. Click to open that note there, in a new tab.' : 'This word was used in another app’s note. Hover for the passage it was met in.') })),
          ]
          return { ...f, children: items.length ? <PropertyDoors items={items} /> : null }
        }
        if (p.id === 'usage') {
          if (shared) return { ...f, children: null }
          if (!usage) return { ...f, children: <span className="dt-gloss">counting…</span> }
          const counts = usage.ink?.counts || {}
          const own = counts[word.id] || { uses: 0, notes: 0 }
          const syn = (word.synonyms || []).filter(r => !r.group && !r.user).map(r => ({ term: r.term, ...(counts[r.id] || { uses: 0, notes: 0 }) })).filter(r => r.uses)
          const via = syn.reduce((n, r) => n + r.uses, 0)
          const cites = (word.usedIn || []).length
          const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`
          return { ...f, children: <span className="dt-usage">
            {usage.ink
              ? <span>{plural(own.uses, 'use')} in Ink{own.uses ? `, in ${plural(own.notes, 'note')}` : ''}</span>
              : <span className="dt-gloss">Ink could not be read{usage.error ? ` — ${usage.error}` : ''}</span>}
            {via > 0 && <span>+ {via} via synonyms ({syn.map(r => `${r.term} ${r.uses}`).join(', ')}) · {own.uses + via} in all</span>}
            {cites > 0 && <span>cited by {plural(cites, 'word')} here</span>}
            {usage.ink?.sealed > 0 && <span className="dt-gloss">{plural(usage.ink.sealed, 'encrypted note')} not counted</span>}
          </span> }
        }
        if (p.id === 'tags') return { ...f, children: <PropertyChips values={word.tags || []} readOnly={readOnly} commitOnBlur
          placeholder="Add…" emptyPlaceholder="Add a tag…" onChange={tags => onChange({ tags })} /> }
        if (p.id === 'aliases') return { ...f, children: <PropertyChips values={word.aliases || []} readOnly={readOnly} commitOnBlur
          placeholder="Add…" emptyPlaceholder="Add another form…" onChange={aliases => onChange({ aliases })} /> }
        if (p.id === 'synonyms' || p.id === 'antonyms') return { ...f, children: <Links field={p.id} word={word} words={words}
          dictionaries={dictionaries} lens={lens} readOnly={readOnly || shared || !onLink} onLink={onLink} onUnlink={onUnlink}
          onOpen={onOpen} onNotice={onNotice} /> }
        if (p.id === 'stance') return { ...f, children: <PropertyDoors items={[{ key: 'st', label: avoids ? 'Anti-word' : 'Word',
          onOpen: readOnly || shared ? undefined : () => setStance(avoids ? 'held' : 'avoided'),
          ...qh(avoids ? 'Anti-word' : 'Word', avoids ? 'You avoid this word. Press to hold it again.' : 'You hold this word. Press to avoid it instead — it moves to your anti-dictionaries.') }]} /> }
        if (p.id === 'rather') {
          if (avoids) return { ...f, label: 'Rather', children: <PropertyChips values={(word.rather || []).map(r => r.term)} readOnly={readOnly} commitOnBlur
            placeholder="Add…" emptyPlaceholder="A word you’d rather use…" onChange={setRather}
            onPick={t => { const w = heldNamed(t); if (w) onOpen(w.id) }} pickLabel={t => heldNamed(t) ? `Open ${t}` : `${t} is not in your dictionary yet`} /> }
          return { ...f, label: 'Instead of', help: 'The anti-words that name this one as what you would rather say. Each is a door.',
            children: insteadOf.length ? <PropertyDoors items={insteadOf.map(w => ({ key: w.id, label: w.name, onOpen: () => onOpen(w.id),
              ...qh(w.name, 'An anti-word that names this one as its rather. Click to open it.') }))} /> : null }
        }
        if (p.id === 'replacement') {
          // one word, and only a word you hold: the translation always lands in your vocabulary
          if (!avoids) return { ...f, children: null }
          const cur = word.replacement ? [word.replacement.term] : []
          return { ...f, children: <PropertyChips values={cur} readOnly={readOnly} commitOnBlur
            placeholder="Change…" emptyPlaceholder="A word from your dictionary…"
            onChange={list => {
              const t = list.filter(x => !cur.includes(x)).pop() ?? (list.length ? list[list.length - 1] : null)
              if (!t) return onChange?.({ replacement: null })
              const w = heldNamed(t)
              if (!w || String(w.id).startsWith('local_')) return onNotice?.(`“${t}” is not a word in your dictionary yet. Replace with takes a word you hold.`)
              onChange?.({ replacement: { term: w.name, id: w.id } })
            }}
            onPick={t => { const w = heldNamed(t); if (w) onOpen(w.id) }} pickLabel={t => `Open ${t}`} /> }
        }
        if (p.id === 'commitment') {
          /* the stampers, faces stacked in the order they came; the platform
             hands a third-party app no other member's photo, so a stamper
             wears the kit's initial avatar — and you wear your own. A stamp is
             a write on the node, so a read-only grant carries none. */
          const people = live.map(c => ({ id: c.user_id, name: c.name || c.user_id,
            label: `${c.name || c.user_id}${c.at ? ` · ${formatPropertyDate(c.at)}` : ''}`,
            src: me && c.user_id === me.user_id ? me.avatarUrl : undefined, mine: !!(me && c.user_id === me.user_id) }))
          const action = !draft && !readOnly && <PropertyDoors items={[mine
            ? { key: 'un', label: 'Uncommit', onOpen: onUnstamp, ...qh('Uncommit', 'Withdraw your commitment to this definition. The word stays; only your stamp is removed.') }
            : { key: 'do', label: 'Commit', onOpen: onStamp, ...qh('Commit', 'Stamp this definition as it stands. If the words are later edited, your stamp is voided and the drift shows in the history.') }]} />
          return { ...f, children: <PropertyFaces people={people} count={live.length} action={action} /> }
        }
        return f
      })} />
  )

  return <>
    {/* THE WORD'S RIGHT RAIL (owner call 2026-09-23: "I want it in the
        dictionary too") — the kit's <EndRail>, the notebook's own: Focus
        (the way in and back), ⋯ for everything that acts on the word (the
        corner MENU retired into it), the history where the word can be
        written, and the counts at the foot. */}
    <EndRail label="Word" items={[
      // qhBody: what the Help card says over each block
      onFocusChange && { id: 'focus', label: focus ? 'Leave focus' : 'Focus', glyph: <FocusGlyph active={!!focus} />, active: !!focus, onClick: () => onFocusChange(!focus),
        qhBody: focus ? 'Brings the rails, the list and the dock back.' : 'Slides the rails, the list and the dock away so the word stands alone. Press again to bring them back.' },
      !draft && { id: 'more', label: 'More', glyph: <LucideGlyph name="more" />, items: more,
        qhBody: shared
          ? 'Everything else for this word: share, version history, properties, copy it into your dictionary, export, or copy its link.'
          : 'Everything else for this word: share, version history, properties, duplicate, export, copy its link, or delete it.' },
      // a reader's rail is the same rail: the history dims, it does not vanish
      { id: 'undo', label: 'Undo', glyph: <LucideGlyph name="undo" />, disabled: readOnly || !ed()?.can().undo(), onClick: () => ed()?.chain().focus().undo().run(),
        qhBody: 'Undoes your last change to the word. ⌘Z. Dimmed when there is nothing to undo or the word is not yours to edit.' },
      { id: 'redo', label: 'Redo', glyph: <LucideGlyph name="redo" />, disabled: readOnly || !ed()?.can().redo(), onClick: () => ed()?.chain().focus().redo().run(),
        qhBody: 'Redoes the change you just undid. ⇧⌘Z. Dimmed when there is nothing to redo or the word is not yours to edit.' },
      { id: 'words', label: 'Word', stat: counts.words.toLocaleString(), foot: true,
        qhTitle: 'Words', qhBody: 'How many words the page holds, the word itself included. It counts as you type.' },
      { id: 'chars', label: 'Char', stat: counts.chars.toLocaleString(), foot: true,
        qhTitle: 'Characters', qhBody: 'How many characters the page holds, spaces included. It counts as you type.' },
    ].filter(Boolean)} />
    <article className={`dt-sheet${inherited ? ' has-inherited' : ''}`}
      {...qh('The word and what it means', 'One document: the word stands at the top, its properties under it, and everything below is the definition, in prose. Committed words you have already defined are underlined wherever they appear — hover one for its card, click it to go there. Type @ to insert one. Select a passage and press Aa to define it as a new word.')}>
    {/* the card over a dotted word. The kit marks the term and says when the
        pointer is on one; what the card SAYS is this app's, because only the
        app knows whose sense it is. */}
    <TermTip at={tip?.at} word={tip && terms.find(t => t.id === tip.id)} onOpen={onOpenSlug}
      onHold={holdTip} onRelease={releaseTip} />
    {!draft && <ShareSheet open={shareOpen} onClose={() => setShareOpen(false)} word={word}
      groups={groups} activeGroup={activeGroup} onCopy={onCopy} />}
    {!draft && <ExportSheet open={exportOpen} onClose={() => setExportOpen(false)} word={{ ...word, name: name || word.name, body: body || word.body, text: text || word.text }} terms={terms} />}
    {!draft && <History open={histOpen} onClose={() => setHistOpen(false)} word={word} />}

    {/* WHOSE WORD THIS IS, before the word: a friend's page opens on their
        name and what their grant lets you do — the one line that the moment
        definitions are shared becomes the most important on the page */}
    {avoids && <p className="dt-sheet__stance" {...qh('An anti-word', 'You refuse this word. What you write here is why; its Rather row names what you would say instead. Every editor that reads your dictionary catches it in red.')}>Anti-word{(word.rather || []).length ? <span> · rather {(word.rather || []).map(r => r.term).join(', ')}</span> : null}</p>}
    {shared && <p className="dt-sheet__byline">
      <Avatar name={ownerName} size="sm" />
      <span>Shared by <strong>{ownerName}</strong>{word.owner.username && <span className="dt-sheet__at">@{word.owner.username}</span>} · {readOnly ? 'read only' : 'you can edit'}</span>
    </p>}

    {/* THE WORD AND WHAT IT MEANS ARE ONE DOCUMENT (crux: "go deep in any
        definition/concept"). The kit's editor — ink's, uprooted 2026-09-18 —
        holds both: the word stands in the head's own field (<NoteHead>, owner
        call 2026-10-12), its ledger in the strip under it, and the body is the
        definition, in prose. onChange hands back a plain
        mirror of the body as well as its HTML; `Used in` and Find read THAT,
        never the markup. */}
    {/* THE WORDS ALREADY DEFINED: marked with a dotted rule wherever one is
        written, and offered on '@' as you type. Only COMMITTED words are fed
        in — committing is the writer saying "this is my term of art", which is
        what makes the mark earned rather than automatic, and what keeps a
        first draft of a definition from underlining itself across the app. */}
    {/* the cell, and then the sheet in its place — the kit's bubble dress on
        both, so the dictionary's gesture and ink's are visibly the same one */}
    <SelectionDefine define={define} source={{ id: word.id, title: name || word.name || null }}
      cellProps={qh('Define this', 'You have a passage selected. Press Aa and the Lexicographer proposes a word for it, in your sense, quoting where it was met.')}
      sheetProps={qh('Define this', 'The Lexicographer reads the passage you selected and drafts a word for it — term, definition, aliases. Edit the draft, then keep it: it lands in the dictionary already committed. Click outside to dismiss.')} />

    <Editor className="dt-editor" docId={word._page || word.id}   /* the PAGE's identity — stable across the local→real id swap, so the kit never resets or re-seats the caret mid-word */
      mode="text"   /* TEXT ONLY (owner call 2026-09-19: "there's a nice simplicity to text only — rip it out completely, don't hide it"): the kit loads no block machinery here — no '/', no '+', no blocks. A definition is its word and what it means, in prose. */
      marks={['italic', 'underline', 'strike', 'code', 'link']}   /* NO BOLD (owner call 2026-09-19: "I shouldn't be able to bold the font"): the definition's weight is the type's; emphasis is italic. Not loaded, so ⌘B does nothing and pasted <strong> lands as plain text. */
      title={name} content={body}
      editable={!readOnly}   /* a friend's word without the edit verb is read as they wrote it */
      definedTerms={[...(prefs.termMarks === false ? [] : terms), ...avoidedTerms]} definedSelfId={word.id}
      termSuggest={prefs.termSuggest !== false}
      termPressOpens   /* a press on a dotted word GOES there (owner call 2026-09-19); the hover card above already says what it means */
      onTermHover={(id, el) => { holdTip(); setTip({ id, at: el.getBoundingClientRect() }) }}
      onTermLeave={releaseTip}
      onTermPick={id => { const w = terms.find(t => t.id === id); if (w) onOpenSlug(w.slug) }}
      titlePlaceholder="the word…"
      bodyPlaceholder={avoids ? 'Why you avoid it, in your terms' : 'What it means, in your terms'}
      autoFocus={draft ? 'title' : undefined}
      properties={properties || null}
      onChange={({ title, html, text }) => {
        setName(title); setBody(html); setText(text)
        const fields = {}
        if (title !== last.current.name) fields.name = title
        if (html !== last.current.body) { fields.body = html; fields.text = text }
        last.current = { name: title, body: html }
        if (Object.keys(fields).length) onChange(fields)
      }} />

    {inherited && <aside className="dt-inherited"
      {...qh('A synonym’s definition', `This word has no definition of its own yet, so it reads ${inherited.name}’s, its synonym. Write one above and it is this word’s own; until then this is what it means here, in Ink and wherever it is read.`)}>
      <span className="dt-inherited__via">via <button type="button" onClick={() => onOpen(inherited.id)}>{inherited.name}</button></span>
      <p className="dt-inherited__body">{inherited.text}</p>
    </aside>}

    {/* COMMITMENT IS VERSIONED: committing stamps the words AS THEY STAND, so
        a later edit is visibly a drift from what was agreed to, not a silent
        rewrite of it. */}
    <div className="dt-sheet__acts">
      {/* a persona's word stands proposed until a member stamps it — the
          stamp is the one act, here as in the plate */}
      {!draft && !readOnly && word.status === 'proposed' && <Button variant="solid" onClick={onStamp}
        {...qh('Commit this definition', 'This word was offered by the Lexicographer and is not yet yours. Committing stamps it as it stands; until then it sits dim in the list and is not marked or offered as you write.')}>Commit this definition</Button>}
    </div>

  </article>
  </>
}
